import { useCallback, useMemo } from 'react';
import { useField } from 'formik';
import moment, { Moment } from 'moment';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

interface Props {
    name: string;
    allowEmpty?: boolean;
    format?: string;
    label?: string;
    className?: string;
    minDate?: Moment;
    maxDate?: Moment;
    utc?: boolean;
    shouldDisableDate?: (day: moment.Moment) => boolean;
    onChangeCallback?: (value: string) => void;
}

export const FormDatePicker = ({
    name,
    allowEmpty = false,
    format = 'MM/DD/YYYY',
    utc = false,
    onChangeCallback,
    ...props
}: Props) => {
    const [field] = useField(name);

    const handleChange = useCallback(
        (date: moment.Moment | null) => {
            if (date) {
                field.onChange({ target: { name, value: date.format(format) } });
            }

            if (isFunction(onChangeCallback)) {
                onChangeCallback(date ? date.toISOString() : '');
            }
        },
        [field, format, name, onChangeCallback],
    );

    const value = useMemo(() => {
        if (isEmpty(field.value) && allowEmpty) {
            return null;
        }
        return utc ? moment.utc(field.value) : moment(field.value);
    }, [field.value, utc, allowEmpty]);

    return (
        <DatePicker
            format={format}
            name={name}
            value={value}
            onChange={handleChange}
            slotProps={{ textField: { size: 'small' } }}
            {...props}
        />
    );
};
